import type { OrderEventDocumentRef } from '@klear/upload/order-event-documents'
import { assignTrimmedField } from '@klear/schema/patch'
import { type Incoterm } from '@klear/constants/options'
import { PERMISSION_ENTITY_PURCHASE_ORDER } from '@klear/constants'
import { KlearDate } from '@klear/date'
import { Money } from '@klear/money'
import isString from 'lodash/isString'
import values from 'lodash/values'
import type { PurchaseOrder } from '@klear/purchase-order/purchase-order'
import type { PurchaseOrderCreateWire, PurchaseOrderUpdateWire } from '@klear/purchase-order/purchase-order.wire'

export type PurchaseOrderForm = {
  number: string
  amount: Money
  effectiveDate: KlearDate
  endDate: KlearDate
  incoterm: string
  notes: string
}

export type PurchaseOrderFormErrorMap = Record<string, string | undefined>

export type PurchaseOrderEventData = Partial<Omit<PurchaseOrderCreateWire, 'orderId'>>

export type PurchaseOrderFormDirty = {
  number?: boolean
  amount?: boolean
  effectiveDate?: boolean
  endDate?: boolean
  incoterm?: boolean
  notes?: boolean
}

export const EMPTY_PURCHASE_ORDER_FORM: PurchaseOrderForm = {
  number: '',
  amount: new Money(),
  effectiveDate: KlearDate.today(),
  endDate: KlearDate.today(),
  incoterm: '',
  notes: ''
}

function parseDate(value: unknown): KlearDate | undefined {
  if (value instanceof KlearDate) return value
  if (isString(value) && value.length > 0) return KlearDate.parse(value)
  return undefined
}

export function purchaseOrderFormFromStored(data: PurchaseOrderEventData | null): PurchaseOrderForm {
  if (data == null)
    return { ...EMPTY_PURCHASE_ORDER_FORM, effectiveDate: KlearDate.today(), endDate: KlearDate.today() }

  const effectiveDate = parseDate(data.effectiveDate) ?? KlearDate.today()
  return {
    number: data.number ?? '',
    amount: data.amount != null ? new Money(data.amount) : new Money(),
    effectiveDate,
    endDate: parseDate(data.endDate) ?? effectiveDate,
    incoterm: data.incoterm ?? '',
    notes: data.notes ?? ''
  }
}

export function validatePurchaseOrderForm(form: PurchaseOrderForm): PurchaseOrderFormErrorMap {
  const errors: PurchaseOrderFormErrorMap = {}
  if (!form.amount.greaterThan(form.amount.zero)) {
    errors.amount = 'Amount is required'
  }
  if (form.endDate.isBefore(form.effectiveDate, 'day')) {
    errors.endDate = 'Target delivery date cannot be before the issue date'
  }
  return errors
}

export function hasPurchaseOrderFormErrors(errors: PurchaseOrderFormErrorMap): boolean {
  return values(errors).some(Boolean)
}

export function clearPurchaseOrderFormErrors(
  errors: PurchaseOrderFormErrorMap,
  paths: readonly (keyof PurchaseOrderForm | string)[]
): PurchaseOrderFormErrorMap {
  const next = { ...errors }
  for (const path of paths) {
    delete next[path]
  }
  return next
}

export type PurchaseOrderFieldChange = <K extends keyof PurchaseOrderForm>(
  field: K,
  value: PurchaseOrderForm[K]
) => void

export function purchaseOrderFormToCreateWire(form: PurchaseOrderForm, orderId: number): PurchaseOrderCreateWire {
  const wire: PurchaseOrderCreateWire = {
    orderId,
    amount: form.amount.toJSON(),
    effectiveDate: form.effectiveDate.toJSON(),
    endDate: form.endDate.toJSON()
  }

  assignTrimmedField(wire, 'number', form.number)
  assignTrimmedField(wire, 'incoterm', form.incoterm)
  assignTrimmedField(wire, 'notes', form.notes)

  return wire
}

export function purchaseOrderFormToUpdateWire(
  form: PurchaseOrderForm,
  dirty: PurchaseOrderFormDirty
): PurchaseOrderUpdateWire {
  const wire: PurchaseOrderUpdateWire = {}

  if (dirty.number) wire.number = form.number.trim() || null
  if (dirty.amount) wire.amount = form.amount.toJSON()
  if (dirty.effectiveDate) wire.effectiveDate = form.effectiveDate.toJSON()
  if (dirty.endDate) wire.endDate = form.endDate.toJSON()
  if (dirty.incoterm) wire.incoterm = form.incoterm.trim() || null
  if (dirty.notes) wire.notes = form.notes.trim() || null

  return wire
}

export type PurchaseOrderTimelineForm = {
  effectiveDate: KlearDate
  endDate: KlearDate
  number?: string
  notes?: string
  incoterm?: Incoterm
  amount: Money
}

export type PurchaseOrderEventFormValues = PurchaseOrderTimelineForm & {
  type: typeof PERMISSION_ENTITY_PURCHASE_ORDER
  documents?: OrderEventDocumentRef[]
  files?: File[]
}

export type PurchaseOrderTimelineFormErrorMap = Record<string, string | undefined>

export type PurchaseOrderParentDefaults = {
  amount?: Money
  endDate?: KlearDate
}

export function parentDefaultsFromPurchaseOrder(read: PurchaseOrder): PurchaseOrderParentDefaults {
  return {
    amount: read.amount,
    endDate: read.endDate
  }
}

export function purchaseOrderToEventForm(read: PurchaseOrder): PurchaseOrderEventFormValues {
  return {
    type: PERMISSION_ENTITY_PURCHASE_ORDER,
    effectiveDate: read.effectiveDate,
    endDate: read.endDate,
    number: read.number,
    notes: read.notes,
    documents: [],
    incoterm: (read.incoterm as Incoterm | undefined) ?? undefined,
    amount: read.amount
  }
}

export function createPurchaseOrderEventForm(): PurchaseOrderEventFormValues {
  return {
    type: PERMISSION_ENTITY_PURCHASE_ORDER,
    effectiveDate: KlearDate.today(),
    notes: '',
    amount: new Money(),
    endDate: KlearDate.today(),
    documents: []
  }
}

export function validatePurchaseOrderEventForm(form: PurchaseOrderTimelineForm): PurchaseOrderTimelineFormErrorMap {
  const errors: PurchaseOrderTimelineFormErrorMap = {}
  const amount = form.amount
  if (amount == null || !amount.greaterThan(amount.zero)) {
    errors.amount = 'Amount is required'
  }
  if (form.endDate && form.effectiveDate && form.endDate.isBefore(form.effectiveDate, 'day')) {
    errors.endDate = 'Target delivery date cannot be before the issue date'
  }
  return errors
}

export function purchaseOrderEventFormToCreateWire(
  form: PurchaseOrderTimelineForm,
  orderId: number
): PurchaseOrderCreateWire {
  const wire: PurchaseOrderCreateWire = {
    orderId,
    amount: form.amount.toJSON(),
    effectiveDate: form.effectiveDate.toJSON(),
    endDate: form.endDate.toJSON()
  }
  if (form.number) wire.number = form.number
  if (form.incoterm) wire.incoterm = form.incoterm
  if (form.notes) wire.notes = form.notes
  return wire
}

export function purchaseOrderEventFormToUpdateWire(form: PurchaseOrderTimelineForm): PurchaseOrderUpdateWire {
  return {
    amount: form.amount.toJSON(),
    effectiveDate: form.effectiveDate.toJSON(),
    endDate: form.endDate.toJSON(),
    number: form.number?.trim() || null,
    incoterm: form.incoterm?.trim() || null,
    notes: form.notes?.trim() || null
  }
}
